/**
 * Currency Formatting Utility
 *
 * IMPORTANT: This file prevents locale-based number formatting issues
 * that were causing prices to display incorrectly in Bangladesh
 * (e.g., 25,000 showing as 250,000)
 */

/**
 * Format number as BDT currency with commas
 * Uses manual comma insertion to avoid browser locale issues
 *
 * @param amount - Number or string to format
 * @returns Formatted string with commas (e.g., "25,000")
 *
 * @example
 * formatBDT(25000)     → "25,000"
 * formatBDT(225000)    → "225,000"
 * formatBDT(37500)     → "37,500"
 * formatBDT("45000")   → "45,000"
 * formatBDT(1234567)   → "1,234,567"
 */
export const formatBDT = (
  amount: number | string | undefined | null
): string => {
  // Handle null/undefined
  if (amount === null || amount === undefined) {
    return "0";
  }

  // Convert to number if string
  let num: number;
  if (typeof amount === "string") {
    // Remove any existing commas or spaces
    const cleaned = amount.replace(/[,\s]/g, "");
    num = parseFloat(cleaned);
  } else {
    num = amount;
  }

  // Check if valid number
  if (isNaN(num)) {
    console.warn("formatBDT: Invalid number:", amount);
    return "0";
  }

  // Round to integer (no decimals for BDT)
  const rounded = Math.round(num);

  // Convert to string and add commas manually
  // This regex adds comma every 3 digits from the right
  return rounded.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",");
};

/**
 * Format number with specific decimal places
 * Useful for calculations that need precision
 *
 * @param amount - Number to format
 * @param decimals - Number of decimal places (default: 2)
 * @returns Formatted string with commas and decimals
 *
 * @example
 * formatBDTWithDecimals(37500)      → "37,500.00"
 * formatBDTWithDecimals(37500.75)   → "37,500.75"
 */
export const formatBDTWithDecimals = (
  amount: number | string | undefined | null,
  decimals: number = 2
): string => {
  if (amount === null || amount === undefined) {
    return "0.00";
  }

  let num: number;
  if (typeof amount === "string") {
    const cleaned = amount.replace(/[,\s]/g, "");
    num = parseFloat(cleaned);
  } else {
    num = amount;
  }

  if (isNaN(num)) {
    console.warn("formatBDTWithDecimals: Invalid number:", amount);
    return "0.00";
  }

  // Fix to specified decimals
  const fixed = num.toFixed(decimals);

  // Split into integer and decimal parts
  const [integerPart, decimalPart] = fixed.split(".");

  // Add commas to integer part
  const formattedInteger = integerPart.replace(/\B(?=(\d{3})+(?!\d))/g, ",");

  // Combine
  return decimalPart ? `${formattedInteger}.${decimalPart}` : formattedInteger;
};

/**
 * Parse BDT string back to number
 * Removes commas and converts to number
 *
 * @param formatted - Formatted string like "25,000"
 * @returns Number value
 *
 * @example
 * parseBDT("25,000")    → 25000
 * parseBDT("225,000")   → 225000
 */
export const parseBDT = (formatted: string): number => {
  if (!formatted) return 0;

  // Remove commas and any other non-numeric characters except decimal point
  const cleaned = formatted.replace(/[^\d.]/g, "");
  const num = parseFloat(cleaned);

  return isNaN(num) ? 0 : num;
};
